import { reportingPeriodOptions, statementTypeOptions } from '../constants';
import {
  CanvasSizeEnum,
  LabelPosEnum,
  NrOfDecimalsEnum,
  ReportingPeriodEnum,
  StatementTypeEnum,
} from '../generated-api';
import { SankeyRenderConfig } from './types';

/*
 * Build configuration object for how the rendered Sankey diagram should look like based on the Sankey form inputs.
 * TODO: Infer maxDecimalPlaces internally from the rest of the form data
 */
export const buildRenderConfig = (
  formValues: SankeyFormValues | undefined,
  maxDecimalPlaces: number
): SankeyRenderConfig => {
  const cfg = {
    show_labels: formValues?.show_labels ?? true,
    second_label_line_value_type:
      formValues?.second_label_line_value_type ?? 'amount',
    show_comparison_line: formValues?.show_comparison_line ?? true,
    comparison_line_value_type:
      formValues?.comparison_line_value_type ?? 'percent',
    comparison_line_prefix: formValues?.comparison_line_prefix ?? '',
    comparison_line_suffix: formValues?.comparison_line_suffix ?? ' Y/Y',
    show_title: formValues?.show_title ?? true,
    use_custom_title: formValues?.use_custom_title ?? false,
    custom_title: formValues?.custom_title ?? 'Custom Diagram Title',
    company: formValues?.company_name ?? 'Example Inc',
    fiscal_year: formValues?.fiscal_year ?? 2024,
    reporting_period: getReportingPeriodLabel(
      formValues?.reporting_period ?? ReportingPeriodEnum.FULL_YEAR
    ),
    label_pos: formValues?.label_pos ?? LabelPosEnum.AFTER, // LabelPosEnum.OUTSIDE would be prefered, but there is a bug with png-rendering leftlabels with 3 lines
    statement_type:
      statementTypeOptions.find((o) => o.value === formValues?.statement_type)
        ?.label ?? 'Income Statement',
    canvas_size: formValues?.canvas_size ?? CanvasSizeEnum._3, // Large
    canvas_width: formValues?.canvas_width ?? 960, // Large canvas width: 960,
    canvas_height: formValues?.canvas_height ?? 600, // Large canvas height: 600,
    show_imbalances: formValues?.show_imbalances ?? true,
    show_watermark: formValues?.show_watermark ?? true,

    fadeOpacity: 0.6,
    flow: {
      opacity: 0.45,
    },
    font: {
      family: 'Inter',
      weight: {
        normal: 400,
        bold: 700,
      },
    },
    hint: {
      color: '#7F7F7F',
    },
    label: {
      color: '#000000',
      lineHeightMultiplier: 1.2,
      font: {
        size: formValues?.font_size ?? 14,
      },
    },
    margin: {
      top: 60,
      bottom: 20,
      x: 12,
    },
    maxDecimalPlaces,
    node: {
      width: formValues?.node_width ?? 30,
      height: formValues?.node_height ?? 50,
      spacing: formValues?.node_spacing ?? 85,
    },
    numberStyle: {
      marks: { group: ',', decimal: '.' },
      decimalPlaces:
        formValues?.nr_of_decimals !== NrOfDecimalsEnum.ALL
          ? parseInt(formValues?.nr_of_decimals ?? '0')
          : maxDecimalPlaces,
      trimString: '',
      prefix: formValues?.unit_prefix ?? '',
      suffix: formValues?.unit_suffix ?? '',
    },
    textBackground: {
      normal: {
        fill: '#FFFFFF',
        fillOpacity: 0.55,
        rx: 4,
      },
      highlighted: {
        fill: 'rgb(237, 242, 247)',
        fillOpacity: 1,
        rx: 4,
      },
    },
    title: {
      color: formValues?.title_font_color ?? '#366B87',
      font: {
        size: formValues?.title_font_size ?? 32,
      },
    },
  };

  // Adjust canvas size
  if (cfg.canvas_size === CanvasSizeEnum._1) {
    cfg.canvas_width = 352;
    cfg.canvas_height = 300;
    cfg.margin.top = 44;
  } else if (cfg.canvas_size === CanvasSizeEnum._2) {
    cfg.canvas_width = 800;
    cfg.canvas_height = 480;
    cfg.margin.top = 50;
  } else if (cfg.canvas_size === CanvasSizeEnum._3) {
    cfg.canvas_width = 960;
    cfg.canvas_height = 600;
    cfg.margin.top = 60;
  }

  return cfg;
};

// Returns the human readable label for a given ReportingPeriodEnum value
export const getReportingPeriodLabel = (value: ReportingPeriodEnum) =>
  reportingPeriodOptions.find((o) => o.value === value)?.label ?? 'Full Year';

// Returns the human readable label for a given StatementTypeEnum value
export const getStatementTypeLabel = (value: StatementTypeEnum) =>
  statementTypeOptions.find((o) => o.value === value)?.label ?? '';
